import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Calculator, TrendingDown, ArrowRight } from "lucide-react";
import { useCountUp } from "../hooks/useCountUp";

const formatINR = (n) => {
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)} L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(1)}K`;
  return `₹${Math.round(n)}`;
};

const Slider = ({ label, value, onChange, min, max, step, suffix, testid }) => (
  <div>
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs tracking-wider uppercase text-[#B0B0B0]">
        {label}
      </span>
      <span className="font-mono text-sm text-[#D4AF37]">
        {value}
        {suffix}
      </span>
    </div>
    <input
      type="range"
      data-testid={testid}
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(+e.target.value)}
      className="w-full accent-[#D4AF37] cursor-pointer"
      style={{
        background: `linear-gradient(to right, #D4AF37 0%, #D4AF37 ${
          ((value - min) / (max - min)) * 100
        }%, #222 ${((value - min) / (max - min)) * 100}%, #222 100%)`,
        height: "4px",
        WebkitAppearance: "none",
        borderRadius: "9999px",
      }}
    />
  </div>
);

export const ROICalculator = ({ onCTA }) => {
  const [visitors, setVisitors] = useState(500); // monthly online searchers
  const [conv, setConv] = useState(4); // % conversion improvement
  const [aov, setAov] = useState(2500); // avg. order/ticket value

  // Lost monthly customers = visitors * conv%
  const lostCustomers = useMemo(
    () => Math.round((visitors * conv) / 100),
    [visitors, conv]
  );
  const lostRevenueMonth = useMemo(
    () => lostCustomers * aov,
    [lostCustomers, aov]
  );
  const lostRevenueYear = lostRevenueMonth * 12;

  const [refMonth, animMonth] = useCountUp(lostRevenueMonth, { duration: 600 });
  const [refYear, animYear] = useCountUp(lostRevenueYear, { duration: 800 });
  const [refCust, animCust] = useCountUp(lostCustomers, { duration: 600 });

  // Force re-animate when sliders change by remounting keys
  const k = `${visitors}-${conv}-${aov}`;

  return (
    <section
      id="roi-calculator"
      data-testid="roi-calculator-section"
      className="relative py-24 sm:py-32 border-t border-white/5 bg-[#050505]"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="max-w-2xl mb-14">
          <div className="text-[11px] tracking-[0.2em] uppercase text-[#D4AF37] mb-4 inline-flex items-center gap-2">
            <Calculator className="w-3.5 h-3.5" /> ROI Calculator
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-light text-white">
            How many customers are <br />
            you <span className="gold-text italic">losing</span> without a website?
          </h2>
          <p className="mt-5 text-[#B0B0B0]">
            Move the sliders to see your hidden revenue leak. These are conservative
            industry averages — your real number is usually higher.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          {/* Inputs */}
          <div className="lg:col-span-5 glass rounded-3xl p-7 space-y-7">
            <Slider
              label="Monthly people searching for your service in your city"
              value={visitors}
              onChange={setVisitors}
              min={50}
              max={5000}
              step={50}
              suffix=""
              testid="roi-slider-visitors"
            />
            <Slider
              label="Realistic conversion (% you'd capture with a real site)"
              value={conv}
              onChange={setConv}
              min={1}
              max={15}
              step={1}
              suffix="%"
              testid="roi-slider-conv"
            />
            <Slider
              label="Average value per customer (₹)"
              value={aov}
              onChange={setAov}
              min={200}
              max={50000}
              step={100}
              suffix=""
              testid="roi-slider-aov"
            />
          </div>

          {/* Results */}
          <div className="lg:col-span-7">
            <div
              className="relative rounded-3xl p-8 sm:p-10 overflow-hidden border"
              style={{
                background:
                  "linear-gradient(180deg, rgba(212,175,55,0.10) 0%, rgba(212,175,55,0.02) 100%)",
                borderColor: "rgba(212,175,55,0.35)",
              }}
            >
              <div className="absolute -top-32 -right-20 w-72 h-72 bg-[#D4AF37]/15 blur-3xl rounded-full pointer-events-none" />
              <div className="relative">
                <div className="flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-[#D4AF37]">
                  <TrendingDown className="w-3.5 h-3.5" />
                  You're likely losing
                </div>

                <div className="mt-6 grid sm:grid-cols-2 gap-6">
                  <div ref={refCust}>
                    <div className="text-xs text-[#B0B0B0] uppercase tracking-wider mb-2">
                      Customers / month
                    </div>
                    <div
                      key={"c" + k}
                      data-testid="roi-out-customers"
                      className="font-display text-5xl sm:text-6xl text-white tracking-tighter"
                    >
                      {animCust}
                    </div>
                  </div>
                  <div ref={refMonth}>
                    <div className="text-xs text-[#B0B0B0] uppercase tracking-wider mb-2">
                      Revenue / month
                    </div>
                    <div
                      key={"m" + k}
                      data-testid="roi-out-month"
                      className="font-display text-5xl sm:text-6xl gold-text tracking-tighter"
                    >
                      {formatINR(animMonth)}
                    </div>
                  </div>
                </div>

                <div className="my-7 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />

                <div ref={refYear}>
                  <div className="text-xs text-[#B0B0B0] uppercase tracking-wider mb-2">
                    Lost over a full year
                  </div>
                  <div
                    key={"y" + k}
                    data-testid="roi-out-year"
                    className="font-display text-6xl sm:text-7xl text-white tracking-tighter"
                  >
                    {formatINR(animYear)}
                  </div>
                </div>

                <motion.button
                  data-testid="roi-cta"
                  onClick={onCTA}
                  whileHover={{ scale: 1.02 }}
                  className="btn-gold-glow mt-8 inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#E6C35C] text-black font-semibold rounded-full px-6 py-3.5 transition-all shadow-[0_0_30px_rgba(212,175,55,0.35)]"
                >
                  Stop the leak — book a free call
                  <ArrowRight className="w-4 h-4" />
                </motion.button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
